<script setup lang="ts">
  import { computed } from 'vue';
  import {
    MpfRadioItem,
    MpfRadioOptionItem,
    MpfRadiosProps,
    MpfRadiosEmits,
    mpfRadiosDefaults,
  } from './MpfRadios.props';
  import MpfLabel from '../MpfLabel/MpfLabel.vue';
  import MpfHint from '../MpfHint/MpfHint.vue';

  const props = withDefaults(defineProps<MpfRadiosProps>(), mpfRadiosDefaults);
  const emit = defineEmits<MpfRadiosEmits>();

  const baseId = computed(() => props.id || props.name || 'mpf-radios');
  const inputName = computed(() => props.name || baseId.value);
  const hintId = computed(() => (props.hint ? `${baseId.value}-hint` : undefined));
  const errorId = computed(() => (props.errors ? `${baseId.value}-errors` : undefined));

  const headingTag = computed(() =>
    props.legendHeadingLevel
      ? (`h${props.legendHeadingLevel}` as keyof HTMLElementTagNameMap)
      : 'div',
  );
  const headingSize = computed(() =>
    props.legendHeadingSize ? `mpf-fieldset__legend--${props.legendHeadingSize}` : null,
  );

  const getItemId = (item: MpfRadioOptionItem, index: number) =>
    item.id || `${baseId.value}-${index}`;

  const getItemKey = (item: MpfRadioItem, index: number) =>
    item.type === 'divider' ? `divider-${index}-${item.label}` : `radio-${getItemId(item, index)}`;

  const describedBy = () => {
    const ids: string[] = [];
    if (hintId.value) ids.push(hintId.value);
    if (errorId.value) ids.push(errorId.value);
    if (props.ariaDescribedby) ids.push(props.ariaDescribedby);
    return ids.length ? ids.join(' ') : undefined;
  };

  const onChange = (value: string | number | null, event: Event) => {
    if (props.disabled) return;
    emit('update:modelValue', value);
    emit('change', value, event);
  };

  const onBlur = (event: FocusEvent) => emit('blur', event);
</script>
<template>
  <div class="mpf-form-group mpf-radios" :class="{ 'mpf-form-group--error': !!errors.length }">
    <fieldset class="mpf-fieldset">
      <legend v-if="label" class="mpf-fieldset__legend" :class="headingSize">
        <component :is="headingTag" class="mpf-fieldset__heading">
          {{ label }}
        </component>
      </legend>

      <MpfHint v-if="hint" :hint="hint" :sr-hint="srHint" :id="hintId" />

      <span v-if="errors![0]" class="mpf-error-message" :id="errorId">
        <span class="mpf-u-visually-hidden">Error:</span>
        {{ errors![0] }}
      </span>

      <div class="mpf-radios" data-module="mpf-radios">
        <template v-for="(item, index) in items" :key="getItemKey(item, index)">
          <div v-if="item.type === 'divider'" class="mpf-radios__divider">
            {{ item.label }}
          </div>

          <div v-else class="mpf-radios__item">
            <input
              class="mpf-radios__input"
              type="radio"
              :id="getItemId(item, index)"
              :name="inputName"
              :value="item.value"
              :disabled="disabled || !!item.disabled"
              :aria-describedby="describedBy()"
              @change="onChange(item.value, $event)"
              @blur="onBlur"
              :checked="typeof modelValue === 'string' && modelValue === item.value"
            />

            <mpf-label
              class="mpf-label mpf-radios__label"
              :label="item.label"
              :for="getItemId(item, index)"
              data-private
            />
          </div>
        </template>
      </div>
    </fieldset>
  </div>
</template>

<style lang="scss" scoped>
  @use './MpfRadios.scss';
</style>
